import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { cloneDeep } from 'lodash-es';
import { Observable, Subject, firstValueFrom, takeUntil } from 'rxjs';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { ApiService } from '../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';
import { Course } from '../../../../../models/interfaces/course.interface';
import { CustomFile } from '../../../../../models/interfaces/custom_file.interface';
import { FileResult } from '../../../../../models/interfaces/file_result.interface';
import { UtilsService } from '../../../../../shared/utils/utils.service';

import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { DomSanitizer } from '@angular/platform-browser';
import { EnfafCheckboxComponent } from '../../../../../components/enfaf-checkbox/enfaf-checkbox.component';
import { EnfafIconComponent } from '../../../../../components/enfaf-icon/enfaf-icon.component';
import { EnfafImgDraggerComponent } from '../../../../../components/enfaf-img-dragger/enfaf-img-dragger.component';
import { EnfafInputFileDraggerLightComponent } from '../../../../../components/enfaf-input-file-dragger-light/enfaf-input-file-dragger-light.component';
import { EnfafInputSuffixComponent } from '../../../../../components/enfaf-input-suffix/enfaf-input-suffix.component';
import { EnfafInputComponent } from '../../../../../components/enfaf-input/enfaf-input.component';
import { EnfafModalComponent } from '../../../../../components/enfaf-modal/enfaf-modal.component';
import { EnfafSelectLightComponent } from '../../../../../components/enfaf-select-light/enfaf-select-light.component';
import { EnfafSwitchDarkComponent } from '../../../../../components/enfaf-switch-dark/enfaf-switch-dark.component';
import { EnfafTextareaLightComponent } from '../../../../../components/enfaf-textarea-light/enfaf-textarea-light.component';
import { MultiAutocompleteLightComponent, SelectOption } from '../../../../../components/multi-autocomplete-light/multi-autocomplete-light.component';
import { ProductVariant } from '../../../../../models/interfaces/product-variant.interface';
import { ProductVariantsComponent } from '../product-variants/product-variants.component';

@Component({
  selector: 'app-modal-new-product',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSlideToggleModule,
    EnfafImgDraggerComponent,
    EnfafModalComponent,
    EnfafInputComponent,
    EnfafInputSuffixComponent,
    EnfafSelectLightComponent,
    EnfafSwitchDarkComponent,
    EnfafInputFileDraggerLightComponent,
    ProductVariantsComponent,
    EnfafCheckboxComponent,
    EnfafIconComponent,
    EnfafTextareaLightComponent,
    MultiAutocompleteLightComponent
  ],
  templateUrl: './modal-new-product.component.html',
  styleUrl: './modal-new-product.component.scss'
})
export class ModalNewProductComponent implements OnInit, OnDestroy {
  productForm: FormGroup = new FormGroup({});
  availableCourses: Array<Course> = [];
  courseOptions: Array<{ value: any; viewValue: string }> = [];
  showCloseAlert = false;
  isSavingProduct = false;
  agreementToUpload: CustomFile | null = null;
  imageToUpload: File | null = null;
  imageToUploadSrc: string | null = null;
  selectedCourses: SelectOption[] = [];

  variants: ProductVariant[] = [];

  private unsubscribe$ = new Subject<void>();
  private isComponentDestroyed = false;

  get hasReducedFields(): boolean {
    return this.productForm.get('hasReducedFields')?.value;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalNewProductComponent>,
    private alert: AlertService,
    private api: ApiService,
    private toast: ToastService,
    private utils: UtilsService,
    private sanitizer: DomSanitizer,
    private iconRegistry: MatIconRegistry
  ) {
    this.availableCourses = [];
    this.courseOptions = [];

    try {
      this.iconRegistry.addSvgIcon('euro-sign', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icon/euro-sign.svg'));
      this.iconRegistry.addSvgIcon('percent', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icon/percent.svg'));
    } catch (error) {}
  }

  ngOnInit(): void {
    try {
      if (!this.availableCourses) {
        this.availableCourses = [];
      }

      this.initForm();
    } catch (error) {
      this.toast.presentErrorToast('Error inicializando el formulario');
      this.closeModal();
    }
  }

  ngOnDestroy(): void {
    this.isComponentDestroyed = true;
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  async confirmCloseModal(): Promise<any> {
    if (this.showCloseAlert) {
      const closeModal = await this.alert.presentCloseWindowAlert();
      return closeModal ? this.closeModal() : null;
    }

    this.closeModal();
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  initForm(): void {
    const receivedData = this.data ? cloneDeep(this.data) : null;

    this.availableCourses = receivedData?.availableCourses || [];

    if (!this.availableCourses?.length) {
      this.toast.presentErrorToast('No hay cursos disponibles para crear un producto');
      this.closeModal();
      return;
    }

    this.updateCourseOptions();

    this.productForm = new FormGroup({
      name: new FormControl(null, [Validators.required, Validators.minLength(3), Validators.maxLength(100)]),
      price: new FormControl(null, [Validators.required, Validators.min(0), Validators.max(10000)]),
      iva: new FormControl(null, [Validators.min(1), Validators.max(100)]),
      sku: new FormControl(null, [Validators.required, Validators.pattern(/^[A-Z0-9_]+$/), Validators.minLength(3), Validators.maxLength(50)]),
      redirectUrl: new FormControl(null, [this.urlValidator.bind(this)]),
      urlGoogleSheets: new FormControl(null, [this.urlValidator.bind(this)]),
      isSubscription: new FormControl(false, Validators.required),
      frequency: new FormControl({ value: null, disabled: true }, [Validators.min(1), Validators.max(12)]),
      frequencyGranularity: new FormControl({ value: null, disabled: true }),
      totalCycles: new FormControl({ value: null, disabled: true }, [Validators.min(1), Validators.max(999)]),
      signUpFee: new FormControl({ value: null, disabled: true }, [Validators.min(0), Validators.max(10000)]),
      productImageUrl: new FormControl(null),
      hasVariants: new FormControl(false),
      hasReducedFields: new FormControl(false),
      reducedFieldsDescription: new FormControl(null)
    });

    this.listenForChanges();
  }

  private updateCourseOptions(): void {
    if (!this.availableCourses || !Array.isArray(this.availableCourses)) {
      this.courseOptions = [];
      return;
    }

    this.courseOptions = this.availableCourses.map((course) => ({
      value: course.courseId,
      viewValue: course.name
    }));
  }

  urlValidator(control: any) {
    if (!control.value) return null; // Campo opcional

    const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
    return urlPattern.test(control.value) ? null : { invalidUrl: true };
  }

  listenForChanges(): void {
    this.productForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.showCloseAlert = true;
    });

    this.productForm.controls['price'].valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      if (value === 0) {
        this.productForm.controls['isSubscription'].setValue(false);
        this.productForm.controls['isSubscription'].disable({ emitEvent: false });
        this.disableSubscriptionFields();
      } else {
        this.productForm.controls['isSubscription'].enable({ emitEvent: false });
      }
    });

    this.productForm.controls['isSubscription'].valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      if (value) {
        this.enableSubscriptionFields();
      } else {
        this.disableSubscriptionFields();
      }
    });
  }

  enableSubscriptionFields(): void {
    this.productForm.controls['frequency'].enable({ emitEvent: false });
    this.productForm.controls['frequencyGranularity'].enable({ emitEvent: false });
    this.productForm.controls['totalCycles'].enable({ emitEvent: false });
    this.productForm.controls['signUpFee'].enable({ emitEvent: false });

    this.productForm.controls['frequency'].setValidators([Validators.required, Validators.min(1), Validators.max(12)]);
    this.productForm.controls['frequencyGranularity'].setValidators([Validators.required]);
    this.productForm.controls['totalCycles'].setValidators([Validators.required, Validators.min(1), Validators.max(999)]);
    this.productForm.controls['signUpFee'].setValidators([Validators.required, Validators.min(0), Validators.max(10000)]);

    this.productForm.controls['frequency'].updateValueAndValidity();
    this.productForm.controls['frequencyGranularity'].updateValueAndValidity();
    this.productForm.controls['totalCycles'].updateValueAndValidity();
    this.productForm.controls['signUpFee'].updateValueAndValidity();
  }

  disableSubscriptionFields(): void {
    this.productForm.controls['frequency'].disable({ emitEvent: false });
    this.productForm.controls['frequencyGranularity'].disable({ emitEvent: false });
    this.productForm.controls['totalCycles'].disable({ emitEvent: false });
    this.productForm.controls['signUpFee'].disable({ emitEvent: false });

    this.productForm.controls['frequency'].setValue(null);
    this.productForm.controls['frequencyGranularity'].setValue(null);
    this.productForm.controls['totalCycles'].setValue(null);
    this.productForm.controls['signUpFee'].setValue(null);

    this.productForm.controls['frequency'].setValidators([Validators.min(1), Validators.max(12)]);
    this.productForm.controls['frequencyGranularity'].setValidators(null);
    this.productForm.controls['totalCycles'].setValidators([Validators.min(1), Validators.max(999)]);
    this.productForm.controls['signUpFee'].setValidators([Validators.min(0), Validators.max(10000)]);

    this.productForm.controls['frequency'].updateValueAndValidity();
    this.productForm.controls['frequencyGranularity'].updateValueAndValidity();
    this.productForm.controls['totalCycles'].updateValueAndValidity();
    this.productForm.controls['signUpFee'].updateValueAndValidity();
  }

  onDroppedFiles(files: Array<CustomFile>): void {
    if (!files?.length) return;

    const file = files[0];

    if (!file.file.type.includes('pdf')) {
      this.toast.presentErrorToast('Solo se permiten archivos PDF para el contrato');
      return;
    }

    file.name = this.utils.generateFileName(file.name);
    this.agreementToUpload = file;
  }

  deleteAgreementFile(fileResult: FileResult): void {
    this.agreementToUpload = null;
    this.toast.presentSuccessToast('Archivo eliminado correctamente');
  }

  async saveProduct(): Promise<void> {
    if (this.productForm.invalid) {
      this.markFormGroupTouched(this.productForm);
      return;
    }

    try {
      this.isSavingProduct = true;
      const productFormData = new FormData();

      const body = this.productForm.value;

      if (body.price === 0) {
        body.isSubscription = false;
      }

      if (body.hasVariants && this.variants.length > 0) {
        body.variants = this.variants;
      } else {
        body.hasVariants = false;
        body.variants = [];
      }

      body.courses = this.selectedCourses.map((course) => course.id);

      if (body.signUpFee !== null && body.signUpFee !== undefined) {
        body.signUpFee = Number(body.signUpFee);
      }

      productFormData.append('form', JSON.stringify(body));

      if (this.agreementToUpload) {
        productFormData.append('agreement', this.agreementToUpload.file, this.agreementToUpload.name);
      }

      if (this.imageToUpload) {
        productFormData.append('productImage', this.imageToUpload, this.imageToUpload.name);
      }

      const response = await firstValueFrom(this.api.createProduct(productFormData));

      let successMessage: string;
      if (this.selectedCourses.length === 0) {
        successMessage = `El producto "${body.name}" se ha creado correctamente sin cursos asociados`;
      } else if (this.selectedCourses.length === 1) {
        successMessage = `El producto "${body.name}" para el curso "${this.selectedCourses[0].title}" se ha creado correctamente`;
      } else {
        successMessage = `El producto "${body.name}" se ha creado correctamente con ${this.selectedCourses.length} cursos seleccionados`;
      }

      this.isSavingProduct = false;
      this.closeModal({ productCreated: response.productUpdated });
      this.toast.presentSuccessToast(successMessage);
    } catch (errorResponse: any) {
      this.isSavingProduct = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error creando el producto');
    }
  }

  uploadImage(data: { imageToUpload: File | null; imageToUploadSrc: string | null }): void {
    this.imageToUpload = data.imageToUpload;
    this.imageToUploadSrc = data.imageToUploadSrc;
  }

  deleteImage(): void {
    this.imageToUpload = null;
    this.imageToUploadSrc = null;
    this.productForm.patchValue({
      productImageUrl: null
    });
    this.productForm.get('productImageUrl')?.updateValueAndValidity();
  }

  onVariantsChange(variants: ProductVariant[]): void {
    this.variants = variants;
    this.showCloseAlert = true;
  }

  get shouldDisableConfirmButton(): boolean {
    if (this.isSavingProduct) {
      return true;
    }

    const hasBasicValidationErrors = this.productForm.invalid;

    const hasVariantsEnabled = this.productForm.get('hasVariants')?.value;
    if (hasVariantsEnabled) {
      if (this.variants.length === 0) {
        return true;
      }

      const hasInvalidVariants = this.variants.some((variant) => !variant.name || variant.name.trim() === '' || variant.price < 0);
      if (hasInvalidVariants) {
        return true;
      }
    }

    return hasBasicValidationErrors;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  get nameErrors() {
    const control = this.productForm.get('name');
    if (control?.errors && control.touched) {
      if (control.errors['required']) return 'El nombre del producto es requerido';
      if (control.errors['minlength']) return 'El nombre debe tener al menos 3 caracteres';
      if (control.errors['maxlength']) return 'El nombre no puede exceder 100 caracteres';
    }
    return null;
  }

  get priceErrors() {
    const control = this.productForm.get('price');
    if (control?.errors && control.touched) {
      if (control.errors['required']) return 'El precio es requerido';
      if (control.errors['min']) return 'El precio mínimo es 0';
      if (control.errors['max']) return 'El precio máximo es 10,000';
    }
    return null;
  }

  get ivaErrors() {
    const control = this.productForm.get('iva');
    if (control?.errors && control.touched) {
      if (control.errors['min'] || control.errors['max']) return 'El IVA debe estar entre 1% y 100%';
    }
    return null;
  }

  get skuErrors() {
    const control = this.productForm.get('sku');
    if (control?.errors && control.touched) {
      if (control.errors['required']) return 'El SKU es requerido';
      if (control.errors['pattern']) return 'El SKU solo puede contener letras mayúsculas, números y guiones bajos';
      if (control.errors['minlength']) return 'El SKU debe tener al menos 3 caracteres';
      if (control.errors['maxlength']) return 'El SKU no puede exceder 50 caracteres';
    }
    return null;
  }

  get redirectUrlErrors() {
    const control = this.productForm.get('redirectUrl');
    if (control?.errors && control.touched) {
      if (control.errors['invalidUrl']) return 'La URL de redirección no es válida';
    }
    return null;
  }

  get urlGoogleSheetsErrors() {
    const control = this.productForm.get('urlGoogleSheets');
    if (control?.errors && control.touched) {
      if (control.errors['invalidUrl']) return 'La URL de callback no es válida';
    }
    return null;
  }

  get coursesErrors() {
    const control = this.productForm.get('courses');
    if (control?.errors && control.touched) {
      if (control.errors['required']) return 'Debe seleccionar un curso';
    }
    return null;
  }

  get frequencyErrors() {
    const control = this.productForm.get('frequency');
    if (control?.errors && control.touched) {
      if (control.errors['required']) return 'La frecuencia de cobro es requerida';
      if (control.errors['min'] || control.errors['max']) return 'La frecuencia debe estar entre 1 y 12';
    }
    return null;
  }

  get frequencyGranularityErrors() {
    const control = this.productForm.get('frequencyGranularity');
    if (control?.errors && control.touched) {
      if (control.errors['required']) return 'La granularidad de la frecuencia es requerida';
    }
    return null;
  }

  get totalCyclesErrors() {
    const control = this.productForm.get('totalCycles');
    if (control?.errors && control.touched) {
      if (control.errors['required']) return 'El número de ciclos es requerido';
      if (control.errors['min'] || control.errors['max']) return 'El número de ciclos debe estar entre 1 y 999';
    }
    return null;
  }

  get signUpFeeErrors() {
    const control = this.productForm.get('signUpFee');
    if (control?.errors && control.touched) {
      if (control.errors['required']) return 'El costo de inscripción es requerido';
      if (control.errors['min']) return 'El costo de inscripción mínimo es 0';
      if (control.errors['max']) return 'El costo de inscripción máximo es 10,000';
    }
    return null;
  }

  fetchCourses = (search: string, page: number, pageSize: number): Observable<{ data: SelectOption[]; total: number }> => {
    return new Observable((observer) => {
      firstValueFrom(
        // ENF-2372: a Máster goes in a product by edition, with its real name (its root is «… - 1ª Edición»).
        this.api.fetchAllCourses(false, {
          page: page,
          pageSize,
          search
        })
      )
        .then((response) => {
          const mappedData: SelectOption[] =
            response.courses?.map((course: any) => ({
              id: course.courseId,
              title: course.name,
              selected: false
            })) || [];

          observer.next({
            data: mappedData,
            total: response.pagination?.totalItems || mappedData.length
          });
          observer.complete();
        })
        .catch((error) => {
          if (this.isComponentDestroyed) {
            observer.complete();
            return;
          }
          console.error('Error fetching courses:', error);
          observer.next({
            data: [],
            total: 0
          });
          observer.complete();
        });
    });
  };

  onSelectedCoursesChanged(selected: SelectOption[]): void {
    this.selectedCourses = selected;
  }

  removeCourse(courseId: any): void {
    this.selectedCourses = this.selectedCourses.filter((course) => course.id !== courseId);
  }
}
